import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  LayoutDashboard, 
  FileText, 
  Users, 
  Settings, 
  LogOut,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

const Sidebar = ({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) => {
  const { user, logout, isAdmin } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
    setMobileOpen?.(false);
  };

  const handleNavClick = () => {
    // Close mobile menu when navigating
    setMobileOpen?.(false);
  };

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard'), show: isAdmin },
    { to: '/documents', icon: FileText, label: t('nav.documents'), show: true },
    { to: '/users', icon: Users, label: t('nav.users'), show: isAdmin },
    { to: '/settings', icon: Settings, label: t('nav.settings'), show: true },
  ];

  return (
    <aside className={cn(
      "fixed top-0 h-screen glass-sidebar border-r border-border transition-all duration-300 z-50",
      // Desktop behavior
      "hidden lg:block left-0",
      collapsed ? "lg:w-16" : "lg:w-64",
      // Mobile behavior - slide in from left
      mobileOpen && "block left-0 w-72"
    )}>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            {/* Mobile: always show title and close button */}
            <h1 className={cn(
              "text-xl font-bold text-foreground",
              "lg:hidden block", // Always visible on mobile
              !collapsed && "lg:block" // Desktop: visible when not collapsed
            )}>
              WebProfit
            </h1>
            
            {/* Mobile close button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileOpen?.(false)}
              className="lg:hidden text-muted-foreground hover:text-foreground hover:bg-accent"
            >
              <X className="w-5 h-5" />
            </Button>

            {/* Desktop collapse button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCollapsed(!collapsed)}
              className="hidden lg:flex text-muted-foreground hover:text-foreground hover:bg-accent"
              data-testid="sidebar-toggle"
            >
              {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1">
          {navItems.filter(item => item.show).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={handleNavClick}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200",
                isActive 
                  ? "nav-active" 
                  : "text-muted-foreground hover:text-foreground hover:bg-accent",
                collapsed && "lg:justify-center lg:px-2"
              )}
              data-testid={`nav-${item.to.slice(1)}`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              <span className={cn(
                "font-medium",
                collapsed && "lg:hidden" // Hide text on desktop when collapsed
              )}>
                {item.label}
              </span>
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className="p-3 border-t border-border">
          <div className={cn(
            "px-3 py-2 mb-2",
            collapsed && "lg:hidden" // Hide on desktop when collapsed
          )}>
            <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground capitalize">{t(`role.${user?.role}`)}</p>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className={cn(
              "w-full text-muted-foreground hover:text-red-500 hover:bg-red-500/10 justify-start",
              collapsed && "lg:justify-center lg:px-2"
            )}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span className={cn(
              "ml-3",
              collapsed && "lg:hidden" // Hide text on desktop when collapsed
            )}>
              {t('nav.logout')}
            </span>
          </Button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
